import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { API, authFetch } from "@/App";
import { toast } from "sonner";
import {
  Mail, Phone, Building2, User, Clock, Loader2, RefreshCw,
  ChevronDown, ChevronUp, CheckCircle, Globe, FileText, Send
} from "lucide-react";

const STATUS_CONFIG = {
  new: { label: "New", color: "bg-blue-500/20 text-blue-400 border-blue-500/30" },
  contacted: { label: "Contacted", color: "bg-amber-500/20 text-amber-400 border-amber-500/30" },
  quoted: { label: "Quoted", color: "bg-purple-500/20 text-purple-400 border-purple-500/30" },
  won: { label: "Won", color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" },
  lost: { label: "Lost", color: "bg-red-500/20 text-red-400 border-red-500/30" }
};

export const ServiceQuotePanel = ({ open, onOpenChange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [filter, setFilter] = useState("all");
  const [updating, setUpdating] = useState(false);
  const [editRequest, setEditRequest] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [notes, setNotes] = useState("");

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/consultant/service-quote-requests`);
      if (res.ok) setData(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { if (open) fetchRequests(); }, [open]);

  const handleUpdateStatus = async () => {
    if (!editRequest || !newStatus) return;
    setUpdating(true);
    try {
      const res = await authFetch(`${API}/consultant/service-quote-requests/${editRequest.request_id}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus, notes })
      });
      if (res.ok) {
        toast.success(`Status updated to ${newStatus}`);
        setEditRequest(null);
        setNewStatus("");
        setNotes("");
        fetchRequests();
      } else { toast.error("Failed to update"); }
    } catch (e) { toast.error("Error updating status"); }
    setUpdating(false);
  };

  const filtered = (data?.requests || []).filter(r => filter === "all" || r.status === filter);
  const stats = data?.stats || {};

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="service-quote-panel">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-emerald-600" />
            Service Quote Requests
          </DialogTitle>
          <DialogDescription>
            Manage incoming quote requests from Project Services and Remote Staffing pages
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto space-y-4">
            {/* Stats */}
            <div className="grid grid-cols-5 gap-2">
              {[
                { label: "Total", value: stats.total || 0, color: "text-neutral-900" },
                { label: "New", value: stats.new || 0, color: "text-blue-600" },
                { label: "Contacted", value: stats.contacted || 0, color: "text-amber-600" },
                { label: "Quoted", value: stats.quoted || 0, color: "text-purple-600" },
                { label: "Closed", value: stats.closed || 0, color: "text-emerald-600" }
              ].map((s, i) => (
                <div key={i} className="text-center p-2 bg-neutral-50 rounded-lg">
                  <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
                  <p className="text-xs text-neutral-500">{s.label}</p>
                </div>
              ))}
            </div>

            {/* Filters */}
            <div className="flex items-center gap-2">
              {["all", "new", "contacted", "quoted", "won", "lost"].map(f => (
                <Button
                  key={f}
                  size="sm"
                  variant={filter === f ? "default" : "outline"}
                  onClick={() => setFilter(f)}
                  className={`text-xs capitalize ${filter === f ? "bg-neutral-900 text-white" : ""}`}
                >
                  {f === "all" ? "All" : STATUS_CONFIG[f]?.label || f}
                </Button>
              ))}
              <Button size="sm" variant="ghost" onClick={fetchRequests} className="ml-auto">
                <RefreshCw className="w-3 h-3" />
              </Button>
            </div>

            {/* Request List */}
            {filtered.length > 0 ? filtered.map((req) => {
              const isExpanded = expandedId === req.request_id;
              const statusCfg = STATUS_CONFIG[req.status] || STATUS_CONFIG.new;
              return (
                <Card key={req.request_id} className="border hover:shadow-sm transition-shadow">
                  <CardContent className="pt-4">
                    <div
                      className="flex items-start justify-between cursor-pointer"
                      onClick={() => setExpandedId(isExpanded ? null : req.request_id)}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-neutral-900">{req.name}</span>
                          <Badge className={`text-xs ${statusCfg.color}`}>{statusCfg.label}</Badge>
                          {req.service_category && (
                            <Badge variant="outline" className="text-xs">{req.service_category}</Badge>
                          )}
                        </div>
                        <p className="text-sm text-neutral-500">
                          {req.selected_services?.length || 0} service{(req.selected_services?.length || 0) !== 1 ? "s" : ""} &middot; {req.created_at ? new Date(req.created_at).toLocaleDateString() : ""}
                        </p>
                      </div>
                      {isExpanded ? <ChevronUp className="w-4 h-4 text-neutral-400" /> : <ChevronDown className="w-4 h-4 text-neutral-400" />}
                    </div>

                    {isExpanded && (
                      <div className="mt-4 pt-4 border-t space-y-3">
                        {/* Contact Info */}
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-neutral-400" />
                            <a href={`mailto:${req.email}`} className="text-blue-600 hover:underline">{req.email}</a>
                          </div>
                          {req.phone && (
                            <div className="flex items-center gap-2">
                              <Phone className="w-4 h-4 text-neutral-400" />
                              <span>{req.phone}</span>
                            </div>
                          )}
                          {req.company_name && (
                            <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4 text-neutral-400" />
                              <span>{req.company_name}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-neutral-400" />
                            <span>{req.created_at ? new Date(req.created_at).toLocaleString() : ""}</span>
                          </div>
                        </div>

                        {/* Selected Services */}
                        <div>
                          <p className="text-xs text-neutral-500 mb-1 font-medium">Selected Services:</p>
                          <div className="flex flex-wrap gap-1">
                            {(req.selected_services || []).map((s, i) => (
                              <Badge key={i} variant="outline" className="text-xs">{s}</Badge>
                            ))}
                          </div>
                        </div>

                        {/* Description */}
                        {req.description && (
                          <div>
                            <p className="text-xs text-neutral-500 mb-1 font-medium">Description:</p>
                            <p className="text-sm text-neutral-700 bg-neutral-50 p-3 rounded-lg">{req.description}</p>
                          </div>
                        )}

                        {/* Consultant Notes */}
                        {req.consultant_notes && (
                          <div>
                            <p className="text-xs text-neutral-500 mb-1 font-medium">Your Notes:</p>
                            <p className="text-sm text-neutral-700 bg-amber-50 p-3 rounded-lg border border-amber-200">{req.consultant_notes}</p>
                          </div>
                        )}

                        {/* Actions */}
                        <div className="flex gap-2 pt-2">
                          <Button
                            size="sm"
                            onClick={() => { setEditRequest(req); setNewStatus(req.status); setNotes(req.consultant_notes || ""); }}
                            className="bg-neutral-900 hover:bg-neutral-800 text-white text-xs"
                            data-testid={`update-status-${req.request_id}`}
                          >
                            Update Status
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => window.open(`mailto:${req.email}?subject=Re: Your Service Quote Request - My Biz Consulting`, "_blank")}
                            className="text-xs"
                          >
                            <Mail className="w-3 h-3 mr-1" />
                            Email
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            }) : (
              <div className="text-center py-12 text-neutral-500">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No quote requests yet</p>
              </div>
            )}
          </div>
        )}
      </DialogContent>

      {/* Update Status Dialog */}
      <Dialog open={!!editRequest} onOpenChange={(o) => { if (!o) setEditRequest(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="update-status-dialog">
          <DialogHeader>
            <DialogTitle>Update Quote Status</DialogTitle>
            <DialogDescription>
              {editRequest?.name} &middot; {editRequest?.selected_services?.length || 0} services
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <label className="text-sm font-medium mb-1 block">Status</label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                  <SelectItem value="quoted">Quoted</SelectItem>
                  <SelectItem value="won">Won</SelectItem>
                  <SelectItem value="lost">Lost</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Notes</label>
              <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Internal notes about this quote..." rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditRequest(null)}>Cancel</Button>
            <Button onClick={handleUpdateStatus} disabled={updating} className="bg-neutral-900 text-white">
              {updating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};
